import { useLayoutEffect, useRef, useState, type FormEvent } from "react";
import { api } from "../api";
import type { Worker } from "../types";
import { Select } from "./Select";
import { useAcknowledgedMutation } from "./useAcknowledgedMutation";
import { Button } from "./ui/Button";
import { Field, Input, Textarea } from "./ui/Field";
import { Feedback } from "./ui/Surface";
import { Modal } from "./ui/Modal";
import { settlementWorkerLabel, useSettlementWorkers } from "./useSettlementWorkers";

const signedAmount = (value: string) => {
  const normalized = value.replaceAll(",", "");
  return /^-?\d+$/.test(normalized) && Number.isSafeInteger(Number(normalized)) ? Number(normalized) : null;
};

export function PayrollManualEditor({ workers, actorName, refresh, onSaved, onClose, restoreFocus }: {
  workers: Worker[]; actorName: string; refresh: () => Promise<void>; onSaved: () => void;
  onClose: () => void; restoreFocus: () => HTMLElement | null;
}) {
  const [workerId, setWorkerId] = useState("");
  const [amount, setAmount] = useState("");
  const [content, setContent] = useState("");
  const [attempts, setAttempts] = useState(0);
  const submitted = attempts > 0;
  const formRef = useRef<HTMLFormElement>(null);
  const mutation = useAcknowledgedMutation(refresh, onSaved);
  const blocked = mutation.busy || mutation.committed;
  const workerOptions = useSettlementWorkers(workers);
  const { people } = workerOptions;
  const value = signedAmount(amount);
  const validWorker = people.some(worker => worker.id === workerId);
  const validAmount = value !== null && value !== 0 && Math.abs(value) <= 1_000_000_000;
  const validContent = content.trim().length > 0 && content.trim().length <= 500;
  useLayoutEffect(() => {
    if (!attempts) return;
    formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]:is(input,textarea,button)')?.focus();
  }, [attempts]);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setAttempts(value => value + 1);
    if (blocked || !validWorker || !validAmount || !validContent) return;
    void mutation.execute(() => api.createManualSettlement({ workerId, amount: value!, content: content.trim(), createdBy: actorName }));
  };
  return <Modal title="정산 항목 추가" className="payroll-entry-dialog" busy={mutation.busy} onBack={onClose} onClose={onClose} restoreFocus={restoreFocus}>
    <form ref={formRef} className="payroll-entry-form" noValidate onSubmit={submit} data-testid="manual-settlement-form">
      <div className="payroll-entry-content">
        {workerOptions.error && <Feedback tone="error">{workerOptions.error}<Button size="compact" onClick={workerOptions.retry}>다시 불러오기</Button></Feedback>}
        {mutation.error && <Feedback tone={mutation.committed ? "info" : "error"}>{mutation.error}{mutation.committed && <Button size="compact" onClick={() => void mutation.retry()}>상태 다시 확인</Button>}</Feedback>}
        <div className="payroll-form-grid">
          <Field label="작업자" required error={submitted && !validWorker ? "작업자를 선택해 주세요." : undefined}>
            <Select value={workerId} disabled={blocked} onChange={event => setWorkerId(event.target.value)} data-testid="manual-settlement-worker">
              <option value="">작업자 선택</option>{people.map(worker => <option key={worker.id} value={worker.id}>{settlementWorkerLabel(worker)}</option>)}
            </Select>
          </Field>
          <Field label="금액" required error={submitted && !validAmount ? "0원이 아닌 ±10억원 이내의 금액을 입력해 주세요." : undefined}>
            <Input type="text" inputMode="decimal" disabled={blocked} value={amount} data-testid="manual-settlement-amount"
              onChange={event => {
                const bare = event.target.value.replaceAll(",", "");
                setAmount(/^-?\d+$/.test(bare) && Number.isSafeInteger(Number(bare)) ? Number(bare).toLocaleString("ko-KR") : bare);
              }} placeholder="공제는 - 금액" />
          </Field>
          <Field label="내용" required className="payroll-manual-content" error={submitted && !validContent ? "내용을 1~500자로 입력해 주세요." : undefined}>
            <Textarea disabled={blocked} value={content} maxLength={500} rows={3} data-testid="manual-settlement-content"
              onChange={event => setContent(event.target.value)} />
          </Field>
        </div>
      </div>
      <footer className="payroll-editor-footer"><span role="status">{mutation.phase === "writing" ? "등록 중…" : mutation.phase === "refreshing" ? "목록 확인 중…" : ""}</span><Button variant="primary" type="submit" disabled={blocked}>등록</Button></footer>
    </form>
  </Modal>;
}
